import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaSave, FaTimes,
  FaChevronLeft, FaChevronRight, FaClock, FaCheck, FaUndo, FaUser, FaSearch
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import { buscarClientesPorTermo, documentoDoCliente } from '../others/buscaCliente';

const { API_BASE_URL } = config;

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const TAMANHO_MAX_TITULO = 255;
const TAMANHO_MAX_OBSERVACOES = 2000;

// ── Datas sempre como 'AAAA-MM-DD' montadas com a data LOCAL.
//    (toISOString() usa UTC e, à noite, devolveria o dia seguinte)
function formatarISO(ano, mesIndex, dia) {
  return `${ano}-${String(mesIndex + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

function hojeISO() {
  const h = new Date();
  return formatarISO(h.getFullYear(), h.getMonth(), h.getDate());
}

function dataLonga(iso) {
  const [ano, mes, dia] = iso.split('-').map(Number);
  const texto = new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function nomeMesAno(ano, mesIndex) {
  const texto = new Date(ano, mesIndex, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function textoQuantidade(qtd) {
  return `${qtd} lembrete${qtd === 1 ? '' : 's'}`;
}

// 6 semanas x 7 dias, começando no domingo da semana do dia 1 (altura do calendário sempre igual)
function gerarGrade(ano, mesIndex) {
  const diaSemanaDoPrimeiro = new Date(ano, mesIndex, 1).getDay();
  const celulas = [];
  for (let i = 0; i < 42; i++) {
    const data = new Date(ano, mesIndex, 1 - diaSemanaDoPrimeiro + i);
    celulas.push({
      iso: formatarISO(data.getFullYear(), data.getMonth(), data.getDate()),
      dia: data.getDate(),
      ano: data.getFullYear(),
      mes: data.getMonth(),
      doMes: data.getMonth() === mesIndex
    });
  }
  return celulas;
}

const Agenda = () => {
  const [mesVisivel, setMesVisivel] = useState(() => {
    const h = new Date();
    return { ano: h.getFullYear(), mes: h.getMonth() };
  });
  const [dataSelecionada, setDataSelecionada] = useState(hojeISO());
  const [lembretes, setLembretes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [modal, setModal] = useState({ open: false, lembrete: null });

  const { addToast } = useToast();
  const hoje = hojeISO();

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ══════════════════════════════════════════════════════════════
  // CARGA DE DADOS
  // ══════════════════════════════════════════════════════════════
  // GET    /agenda/mes/{ano}/{mes}   -> lembretes do mês (a agenda é da empresa, todos os usuários veem)
  // POST   /agenda                   -> { titulo, dataLembrete, horaLembrete ('HH:mm'), observacoes }
  // PUT    /agenda/{idLembrete}      -> atualiza lembrete
  // PUT    /agenda/{idLembrete}/concluido -> { concluido } (concluído some da Home, fica riscado aqui)
  // DELETE /agenda/{idLembrete}      -> exclui lembrete
  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/agenda/mes/${mesVisivel.ano}/${mesVisivel.mes + 1}`,
          getAuthHeader()
        );
        if (!cancelado) setLembretes(response.data || []);
      } catch (err) {
        if (!cancelado) {
          console.error('Erro ao carregar agenda:', err);
          setError(err.response?.data?.message || 'Erro ao carregar a agenda');
          setLembretes([]);
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesVisivel, recarga]);

  const porData = lembretes.reduce((mapa, l) => {
    (mapa[l.dataLembrete] = mapa[l.dataLembrete] || []).push(l);
    return mapa;
  }, {});
  const lembretesDoDia = porData[dataSelecionada] || [];
  const grade = gerarGrade(mesVisivel.ano, mesVisivel.mes);

  // ══════════════════════════════════════════════════════════════
  // NAVEGAÇÃO
  // ══════════════════════════════════════════════════════════════
  const irParaMes = (delta) => {
    const novo = new Date(mesVisivel.ano, mesVisivel.mes + delta, 1);
    const ano = novo.getFullYear();
    const mes = novo.getMonth();
    setMesVisivel({ ano, mes });
    // seleciona hoje se o mês for o atual; senão, o dia 1
    setDataSelecionada(hoje.startsWith(formatarISO(ano, mes, 1).slice(0, 7)) ? hoje : formatarISO(ano, mes, 1));
  };

  const irParaHoje = () => {
    const h = new Date();
    setMesVisivel({ ano: h.getFullYear(), mes: h.getMonth() });
    setDataSelecionada(hojeISO());
  };

  const selecionarDia = (celula) => {
    setDataSelecionada(celula.iso);
    if (!celula.doMes) setMesVisivel({ ano: celula.ano, mes: celula.mes });
  };

  // ══════════════════════════════════════════════════════════════
  // CRUD
  // ══════════════════════════════════════════════════════════════
  const handleLembreteSalvo = (salvo) => {
    setModal({ open: false, lembrete: null });
    const [ano, mes] = salvo.dataLembrete.split('-').map(Number);
    setDataSelecionada(salvo.dataLembrete);
    if (ano !== mesVisivel.ano || mes - 1 !== mesVisivel.mes) {
      setMesVisivel({ ano, mes: mes - 1 }); // a mudança de mês já recarrega a lista
    } else {
      setRecarga(r => r + 1);
    }
  };

  const alternarConcluido = async (lembrete) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/agenda/${lembrete.idLembrete}/concluido`,
        { concluido: !lembrete.concluido },
        getAuthHeader()
      );
      setLembretes(lista => lista.map(l => (l.idLembrete === lembrete.idLembrete ? response.data : l)));
      addToast('success', response.data.concluido ? 'Compromisso concluído!' : 'Compromisso reaberto!');
    } catch (err) {
      console.error('Erro ao atualizar lembrete:', err);
      addToast('error', err.response?.data?.message || 'Erro ao atualizar o compromisso');
    }
  };

  const handleLembreteExcluido = () => {
    setModal({ open: false, lembrete: null });
    setRecarga(r => r + 1);
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <FaCalendarAlt style={styles.titleIcon} />
          <h1 style={styles.title}>Agenda</h1>
        </div>

        {error && <div style={styles.errorMessage}>{error}</div>}

        <div style={styles.layout}>
          {/* ══════════ CALENDÁRIO ══════════ */}
          <div style={{ ...styles.painel, ...styles.painelCalendario }}>
            <div style={styles.calHeader}>
              <button onClick={() => irParaMes(-1)} style={styles.navButton} aria-label="Mês anterior">
                <FaChevronLeft />
              </button>
              <div style={styles.calTitulo}>{nomeMesAno(mesVisivel.ano, mesVisivel.mes)}</div>
              <button onClick={() => irParaMes(1)} style={styles.navButton} aria-label="Próximo mês">
                <FaChevronRight />
              </button>
            </div>

            <div style={styles.grade}>
              {DIAS_SEMANA.map(d => (
                <div key={d} style={styles.diaSemana}>{d}</div>
              ))}
              {grade.map(celula => {
                const qtd = celula.doMes ? (porData[celula.iso]?.length || 0) : 0;
                const selecionada = celula.iso === dataSelecionada;
                const ehHoje = celula.iso === hoje;
                return (
                  <button
                    key={celula.iso}
                    onClick={() => selecionarDia(celula)}
                    aria-label={`Dia ${celula.dia}${!celula.doMes ? ' (outro mês)' : ''}${qtd > 0 ? `, ${textoQuantidade(qtd)}` : ''}`}
                    style={{
                      ...styles.celula,
                      ...(!celula.doMes && styles.celulaOutroMes),
                      ...(ehHoje && styles.celulaHoje),
                      ...(selecionada && styles.celulaSelecionada)
                    }}
                  >
                    <span>{celula.dia}</span>
                    {qtd > 0 && (
                      <span style={{ ...styles.badge, ...(selecionada && styles.badgeSelecionada) }}>{qtd}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button onClick={irParaHoje} style={styles.hojeButton}>Ir para hoje</button>
          </div>

          {/* ══════════ LEMBRETES DO DIA ══════════ */}
          <div style={{ ...styles.painel, ...styles.painelDia }}>
            <div style={styles.diaHeader}>
              <div>
                <div style={styles.diaTitulo}>{dataLonga(dataSelecionada)}</div>
                <div style={styles.diaSub}>{textoQuantidade(lembretesDoDia.length)}</div>
              </div>
              <button onClick={() => setModal({ open: true, lembrete: null })} style={styles.primaryButton}>
                <FaPlus /> Novo Lembrete
              </button>
            </div>

            {loading ? (
              <p style={styles.loading}>Carregando...</p>
            ) : lembretesDoDia.length > 0 ? (
              <div style={styles.listaLembretes}>
                {lembretesDoDia.map(l => (
                  <div key={l.idLembrete} style={{ ...styles.lembreteCard, ...(l.concluido && styles.lembreteCardConcluido) }}>
                    <div style={{ ...styles.lembreteHora, ...(l.concluido && styles.lembreteHoraConcluido) }}>{l.horaLembrete || '--:--'}</div>
                    <div style={styles.lembreteConteudo}>
                      <div style={{ ...styles.lembreteTitulo, ...(l.concluido && styles.lembreteTituloConcluido) }}>
                        {l.titulo}
                        {l.concluido && <span style={styles.tagConcluido}>Concluído</span>}
                      </div>
                      {l.nomeCliente && <div style={styles.lembreteCliente}><FaUser style={{ marginRight: 5 }} />{l.nomeCliente}</div>}
                      {l.observacoes && <div style={styles.lembreteTexto}>{l.observacoes}</div>}
                    </div>
                    <button
                      title={l.concluido ? 'Reabrir' : 'Marcar como feito'}
                      aria-label={`${l.concluido ? 'Reabrir' : 'Concluir'} ${l.titulo}`}
                      onClick={() => alternarConcluido(l)}
                      style={styles.iconButton}
                    >
                      {l.concluido ? <FaUndo /> : <FaCheck />}
                    </button>
                    <button
                      title="Editar"
                      aria-label={`Editar ${l.titulo}`}
                      onClick={() => setModal({ open: true, lembrete: l })}
                      style={styles.iconButton}
                    >
                      <FaEdit />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.noResults}>Nenhum lembrete para este dia</p>
            )}
          </div>
        </div>
      </div>

      {modal.open && (
        <ModalLembrete
          lembrete={modal.lembrete}
          dataInicial={dataSelecionada}
          onClose={() => setModal({ open: false, lembrete: null })}
          onSaved={handleLembreteSalvo}
          onDeleted={handleLembreteExcluido}
          addToast={addToast}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODAL: CADASTRAR/EDITAR LEMBRETE
// ══════════════════════════════════════════════════════════════
const ModalLembrete = ({ lembrete, dataInicial, onClose, onSaved, onDeleted, addToast, getAuthHeader }) => {
  const [titulo, setTitulo] = useState(lembrete?.titulo || '');
  const [dataLembrete, setDataLembrete] = useState(lembrete?.dataLembrete || dataInicial);
  const [horaLembrete, setHoraLembrete] = useState(lembrete?.horaLembrete || '');
  const [observacoes, setObservacoes] = useState(lembrete?.observacoes || '');
  // Com quem é o compromisso (opcional): digitado ou escolhido pela busca (nome ou CPF/CNPJ), que liga ao cadastro
  const [nomeCliente, setNomeCliente] = useState(lembrete?.nomeCliente || '');
  const [idCliente, setIdCliente] = useState(lembrete?.idCliente || null);
  const [clientesAchados, setClientesAchados] = useState(null); // null = não buscou
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [errors, setErrors] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [confirmExcluir, setConfirmExcluir] = useState(false);

  const validar = () => {
    const errs = {};
    if (!titulo.trim()) errs.titulo = 'Este campo é obrigatório';
    if (!dataLembrete) errs.dataLembrete = 'Este campo é obrigatório';
    if (!horaLembrete) errs.horaLembrete = 'Este campo é obrigatório';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar()) {
      addToast('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }
    try {
      setSalvando(true);
      const dados = { titulo: titulo.trim(), dataLembrete, horaLembrete, observacoes, nomeCliente: nomeCliente.trim(), idCliente };
      const response = lembrete?.idLembrete
        ? await axios.put(`${API_BASE_URL}/agenda/${lembrete.idLembrete}`, dados, getAuthHeader())
        : await axios.post(`${API_BASE_URL}/agenda`, dados, getAuthHeader());
      addToast('success', lembrete?.idLembrete ? 'Lembrete atualizado com sucesso!' : 'Lembrete cadastrado com sucesso!');
      onSaved(response.data);
    } catch (err) {
      console.error('Erro ao salvar lembrete:', err);
      addToast('error', err.response?.data?.message || 'Erro ao salvar lembrete');
    } finally {
      setSalvando(false);
    }
  };

  const buscarCliente = async () => {
    setBuscandoCliente(true);
    try {
      const achados = await buscarClientesPorTermo(nomeCliente);
      if (achados === null) {
        addToast('warning', 'Digite ao menos 3 letras do nome ou o CPF/CNPJ completo para buscar.');
        return;
      }
      setClientesAchados(achados);
    } catch (err) {
      addToast('error', err.response?.data?.message || 'Erro ao pesquisar clientes');
    } finally {
      setBuscandoCliente(false);
    }
  };

  const escolherCliente = (c) => {
    setNomeCliente(c.nome || '');
    setIdCliente(c.idCliente);
    setClientesAchados(null);
  };

  const handleExcluir = async () => {
    try {
      setSalvando(true);
      await axios.delete(`${API_BASE_URL}/agenda/${lembrete.idLembrete}`, getAuthHeader());
      addToast('success', 'Lembrete excluído com sucesso!');
      onDeleted(lembrete.idLembrete);
    } catch (err) {
      console.error('Erro ao excluir lembrete:', err);
      addToast('error', err.response?.data?.message || 'Erro ao excluir lembrete');
    } finally {
      setSalvando(false);
      setConfirmExcluir(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{lembrete ? 'Editar Lembrete' : 'Novo Lembrete'}</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={salvando}>&times;</button>
        </div>
        <div style={styles.modalContent}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Título*</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex: Vistoria do veículo - Cliente João"
              maxLength={TAMANHO_MAX_TITULO}
              style={{ ...styles.input, ...(errors.titulo && styles.inputError) }}
              disabled={salvando}
            />
            {errors.titulo && <span style={styles.errorText}>{errors.titulo}</span>}
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}><FaUser style={{ marginRight: 6 }} />Cliente</label>
            <div style={styles.linhaCliente}>
              <input
                type="text"
                value={nomeCliente}
                onChange={(e) => { setNomeCliente(e.target.value); setIdCliente(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarCliente(); } }}
                placeholder="Digite o nome ou busque por nome/CPF/CNPJ (opcional)"
                maxLength={255}
                style={{ ...styles.input, flex: 1, minWidth: 0 }}
                disabled={salvando}
              />
              <button type="button" onClick={buscarCliente} style={styles.botaoBuscarCliente} disabled={salvando || buscandoCliente}>
                <FaSearch /> {buscandoCliente ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            {idCliente && <span style={styles.clienteVinculado}><FaCheck /> Cliente do cadastro</span>}
            {clientesAchados && (clientesAchados.length === 0 ? (
              <span style={styles.contador}>Nenhum cliente encontrado. O nome digitado será usado.</span>
            ) : (
              <div style={styles.listaClientes}>
                {clientesAchados.map((c) => (
                  <button type="button" key={c.idCliente} style={styles.itemCliente} onClick={() => escolherCliente(c)}>
                    <FaUser style={{ color: '#008080', flexShrink: 0 }} /> <strong>{c.nome}</strong>
                    <span style={{ color: '#7f8c8d', fontSize: 12 }}>{documentoDoCliente(c)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          <div style={styles.linhaDataHora}>
            <div style={{ ...styles.inputGroup, flex: '1 1 180px' }}>
              <label style={styles.label}><FaCalendarAlt style={{ marginRight: 6 }} />Data*</label>
              <input
                type="date"
                value={dataLembrete}
                onChange={(e) => setDataLembrete(e.target.value)}
                style={{ ...styles.input, ...styles.inputCheio, ...(errors.dataLembrete && styles.inputError) }}
                disabled={salvando}
              />
              {errors.dataLembrete && <span style={styles.errorText}>{errors.dataLembrete}</span>}
            </div>

            <div style={{ ...styles.inputGroup, flex: '1 1 140px' }}>
              <label style={styles.label}><FaClock style={{ marginRight: 6 }} />Horário*</label>
              <input
                type="time"
                value={horaLembrete}
                onChange={(e) => setHoraLembrete(e.target.value)}
                style={{ ...styles.input, ...styles.inputCheio, ...(errors.horaLembrete && styles.inputError) }}
                disabled={salvando}
              />
              {errors.horaLembrete && <span style={styles.errorText}>{errors.horaLembrete}</span>}
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Observações</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Local, telefone do cliente, o que levar... (opcional)"
              rows="5"
              maxLength={TAMANHO_MAX_OBSERVACOES}
              style={styles.textarea}
              disabled={salvando}
            />
            <span style={styles.contador}>{observacoes.length}/{TAMANHO_MAX_OBSERVACOES}</span>
          </div>
        </div>
        <div style={styles.modalFooter}>
          {lembrete?.idLembrete && (
            <button onClick={() => setConfirmExcluir(true)} style={styles.deleteButtonModal} disabled={salvando}>
              <FaTrash /> Excluir
            </button>
          )}
          <button onClick={onClose} style={styles.secondaryButton} disabled={salvando}>
            <FaTimes /> Cancelar
          </button>
          <button onClick={handleSalvar} style={styles.primaryButton} disabled={salvando}>
            {salvando ? 'Salvando...' : (<><FaSave /> {lembrete ? 'Atualizar' : 'Salvar'}</>)}
          </button>
        </div>

        <ConfirmModal
          isOpen={confirmExcluir}
          onClose={() => setConfirmExcluir(false)}
          onConfirm={handleExcluir}
          title="Excluir lembrete"
          message="Tem certeza que deseja excluir este lembrete?"
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta Nexus (#004d4d / #008080 / #20b2aa). Tela trazida da agenda do Truck Control.
// ══════════════════════════════════════════════════════════════
const TEAL_DARK = '#004d4d';
const TEAL = '#008080';
const TEAL_LIGHT_ACCENT = '#20b2aa';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1100px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  titleIcon: {
    fontSize: '26px',
    color: TEAL
  },
  title: {
    fontSize: '22px',
    margin: 0,
    color: TEAL_DARK,
    fontWeight: '700'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px'
  },

  layout: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },
  painel: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    boxSizing: 'border-box'
  },
  painelCalendario: {
    flex: '0 0 auto',
    width: '360px',
    maxWidth: '100%'
  },
  painelDia: {
    flex: '1 1 320px',
    minWidth: 0
  },

  // Calendário
  calHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px'
  },
  calTitulo: {
    fontSize: '16px',
    fontWeight: '700',
    color: TEAL_DARK
  },
  navButton: {
    width: '34px',
    height: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#eaf6f6',
    color: TEAL,
    cursor: 'pointer'
  },
  grade: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px'
  },
  diaSemana: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#7f8c8d',
    padding: '4px 0'
  },
  celula: {
    position: 'relative',
    aspectRatio: '1 / 1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid transparent',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    color: '#2c3e50',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0
  },
  celulaOutroMes: {
    color: '#c3cbd0',
    backgroundColor: 'transparent'
  },
  celulaHoje: {
    border: `2px solid ${TEAL}`
  },
  celulaSelecionada: {
    backgroundColor: TEAL,
    color: 'white',
    border: `2px solid ${TEAL_DARK}`
  },
  badge: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    minWidth: '18px',
    height: '18px',
    padding: '0 4px',
    borderRadius: '9px',
    backgroundColor: TEAL_LIGHT_ACCENT,
    border: '2px solid white',
    color: 'white',
    fontSize: '10px',
    fontWeight: '700',
    lineHeight: '14px',
    textAlign: 'center',
    boxSizing: 'border-box'
  },
  badgeSelecionada: {
    backgroundColor: TEAL_DARK,
    color: 'white'
  },
  hojeButton: {
    width: '100%',
    marginTop: '14px',
    padding: '9px',
    backgroundColor: '#eaf6f6',
    color: TEAL_DARK,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },

  // Lembretes do dia
  diaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    paddingBottom: '14px',
    marginBottom: '14px',
    borderBottom: `2px solid ${TEAL}22`
  },
  diaTitulo: {
    fontSize: '16px',
    fontWeight: '700',
    color: TEAL_DARK
  },
  diaSub: {
    fontSize: '13px',
    color: '#7f8c8d',
    marginTop: '2px'
  },
  listaLembretes: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  lembreteCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 14px',
    backgroundColor: '#f0fdfa',
    border: `1px solid ${TEAL_LIGHT_ACCENT}55`,
    borderLeft: `4px solid ${TEAL}`,
    borderRadius: '8px'
  },
  lembreteHora: {
    flexShrink: 0,
    padding: '3px 8px',
    borderRadius: '6px',
    backgroundColor: TEAL,
    color: 'white',
    fontSize: '13px',
    fontWeight: '700',
    fontVariantNumeric: 'tabular-nums'
  },
  lembreteCardConcluido: {
    backgroundColor: '#f7f7f7',
    border: '1px solid #e0e0e0',
    borderLeft: '4px solid #b0bec5',
    opacity: 0.8
  },
  lembreteHoraConcluido: {
    backgroundColor: '#b0bec5'
  },
  lembreteTituloConcluido: {
    color: '#7f8c8d',
    textDecoration: 'line-through'
  },
  tagConcluido: {
    display: 'inline-block',
    marginLeft: '8px',
    padding: '1px 7px',
    borderRadius: '9px',
    backgroundColor: '#e0e0e0',
    color: '#546e7a',
    fontSize: '11px',
    fontWeight: '700',
    textDecoration: 'none',
    verticalAlign: 'middle'
  },
  lembreteConteudo: {
    flex: 1,
    minWidth: 0
  },
  lembreteTitulo: {
    fontSize: '15px',
    fontWeight: '700',
    color: TEAL_DARK,
    wordBreak: 'break-word'
  },
  // Cliente do compromisso (lista da agenda e campo do modal)
  lembreteCliente: {
    marginTop: '4px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#006666',
    display: 'flex',
    alignItems: 'center'
  },
  linhaCliente: {
    display: 'flex',
    gap: '8px',
    alignItems: 'stretch'
  },
  botaoBuscarCliente: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 14px',
    border: '1px solid #008080',
    borderRadius: '6px',
    background: 'white',
    color: '#006666',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0
  },
  clienteVinculado: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    marginTop: '4px',
    fontSize: '12px',
    color: '#27ae60'
  },
  listaClientes: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '6px',
    border: '1px solid #e3e8e8',
    borderRadius: '6px',
    maxHeight: '180px',
    overflowY: 'auto'
  },
  itemCliente: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 10px',
    border: 'none',
    borderBottom: '1px solid #f0f3f3',
    background: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px'
  },
  lembreteTexto: {
    marginTop: '6px',
    fontSize: '13px',
    color: '#34495e',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  iconButton: {
    width: '32px',
    height: '32px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#eaf6f6',
    color: TEAL,
    cursor: 'pointer'
  },

  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#7f8c8d'
  },
  noResults: {
    textAlign: 'center',
    padding: '30px',
    color: '#95a5a6',
    fontStyle: 'italic'
  },

  // Formulário
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#34495e'
  },
  input: {
    width: '95%',
    padding: '11px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  inputError: {
    border: '1px solid #ff4444'
  },
  linhaDataHora: {
    display: 'flex',
    gap: '14px',
    flexWrap: 'wrap',
    width: '95%'
  },
  inputCheio: {
    width: '100%',
    boxSizing: 'border-box'
  },
  errorText: {
    color: '#ff4444',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  textarea: {
    width: '95%',
    padding: '11px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  contador: {
    display: 'block',
    marginTop: '4px',
    fontSize: '11px',
    color: '#95a5a6',
    textAlign: 'right',
    width: '95%'
  },
  inputGroup: {
    marginBottom: '14px'
  },

  primaryButton: {
    padding: '10px 16px',
    backgroundColor: TEAL,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  secondaryButton: {
    padding: '10px 16px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  deleteButtonModal: {
    padding: '10px 16px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    marginRight: 'auto'
  },

  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 20px',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    fontSize: '18px',
    margin: 0,
    color: TEAL_DARK
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af'
  },
  modalContent: {
    padding: '20px'
  },
  modalFooter: {
    padding: '18px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    flexWrap: 'wrap'
  }
};

export default Agenda;
